/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of Zuum Transportation Inc.
 #*
 #* Copyright Zuum 2015-2021. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from 'app/services/api.service';
import {
  AgingReport,
  AgingReportSearch,
  ApiResponse,
  BillCollectionItem,
  BillCollectionReport,
  BillCollectionSearch,
  Invoice,
  InvoiceFlag,
  InvoiceItem,
  InvoicePaymentInfo,
} from '@core/models';
import { AgingReportPeriod } from '@core/enums';
@Injectable({
  providedIn: 'root',
})
export class SaasInvoiceReportService {
  constructor(private http: HttpClient, private apiService: ApiService) {}

  getBillCollectionReport(searchDTO: BillCollectionSearch): Observable<ApiResponse<BillCollectionReport>> {
    return this.http.post<ApiResponse<BillCollectionReport>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/bill-collection`,
      searchDTO
    );
  }

  getAgingReport(searchDTO: AgingReportSearch): Observable<ApiResponse<AgingReport>> {
    return this.http.post<ApiResponse<AgingReport>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/aging`,
      searchDTO
    );
  }

  getAgingInvoicesForClient(
    clientId: number,
    invoicesPeriod?: AgingReportPeriod
  ): Observable<ApiResponse<BillCollectionItem[]>> {
    return this.http.get<ApiResponse<BillCollectionItem[]>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/aging/invoices/${clientId}/${invoicesPeriod}`
    );
  }

  flagInvoice(invoiceFlag: InvoiceFlag): Observable<ApiResponse<InvoiceFlag>> {
    return this.http.post<ApiResponse<InvoiceFlag>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/flag-invoice`,
      invoiceFlag
    );
  }

  updateInvoiceFlag(invoiceFlag: InvoiceFlag): Observable<ApiResponse<InvoiceFlag>> {
    return this.http.put<ApiResponse<InvoiceFlag>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/flag-invoice`,
      invoiceFlag
    );
  }

  deleteInvoiceFlag(invoiceFlag: InvoiceFlag): Observable<ApiResponse<InvoiceFlag>> {
    return this.http.delete<ApiResponse<InvoiceFlag>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/flag-invoice/${invoiceFlag.id}`
    );
  }

  getInvoiceDetails(id: number): Observable<ApiResponse<Invoice>> {
    return this.http.get<ApiResponse<Invoice>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/invoice-details/${id}`
    );
  }

  addInvoiceItem(
    invoiceItem: InvoiceItem
  ): Observable<
    ApiResponse<{
      invoice: Invoice;
      billCollectionItem: BillCollectionItem;
    }>
  > {
    return this.http.post<
      ApiResponse<{
        invoice: Invoice;
        billCollectionItem: BillCollectionItem;
      }>
    >(`${this.apiService.superAdminApi}/saas-invoice-reports/invoice-item`, invoiceItem);
  }

  deleteInvoiceItem(
    id: number
  ): Observable<
    ApiResponse<{
      invoice: Invoice;
      billCollectionItem: BillCollectionItem;
    }>
  > {
    return this.http.delete<
      ApiResponse<{
        invoice: Invoice;
        billCollectionItem: BillCollectionItem;
      }>
    >(`${this.apiService.superAdminApi}/saas-invoice-reports/invoice-item/${id}`);
  }

  setPaymentInfo(paymentInfo: InvoicePaymentInfo): Observable<ApiResponse<Invoice>> {
    return this.http.post<ApiResponse<Invoice>>(
      `${this.apiService.superAdminApi}/saas-invoice-reports/payment-info`,
      paymentInfo
    );
  }
}
